import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Popover } from '@material-ui/core'
import { Icon } from '@iconify/react'
import { Panel, colors } from 'src/components/app-ui'

const AVATAR_COLORS = [
  ['#E9F1FF', '#0151D5'],
  ['#BFF1CE', '#1D9341'],
  ['#FEE2B3', '#B06E01'],
  ['#FAC6C7', '#A62E30'],
  ['#CEE6FF', '#4179B2'],
]

const GRID = 'minmax(220px, 1fr) 150px 150px 32px'

const useStyles = makeStyles({
  root: {
    overflowX: 'auto',
  },
  title: {
    padding: '18px 20px',
    borderBottom: `1px solid ${colors.border}`,
    fontSize: '15px',
    fontWeight: 600,
  },
  columns: {
    display: 'grid',
    gridTemplateColumns: GRID,
    minWidth: 620,
    gap: '12px',
    padding: '12px 20px',
    borderBottom: `1px solid ${colors.border}`,
    fontSize: '11px',
    fontWeight: 500,
    letterSpacing: '.06em',
    textTransform: 'uppercase',
    color: colors.subtle,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: GRID,
    minWidth: 620,
    alignItems: 'center',
    gap: '12px',
    padding: '12px 20px',
    borderBottom: '1px solid #F1F3F5',
    fontSize: '14px',
  },
  member: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minWidth: 0,
  },
  avatar: {
    width: 34,
    height: 34,
    flex: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    fontSize: '12px',
    fontWeight: 600,
  },
  name: {
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  email: {
    fontSize: '12px',
    color: colors.subtle,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  pendingName: {
    fontWeight: 500,
    color: colors.muted,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  pendingBadge: {
    flex: 'none',
    padding: '2px 8px',
    borderRadius: '999px',
    background: '#FFF4E5',
    fontSize: '11px',
    fontWeight: 600,
    color: colors.warningDark,
  },
  roleWrap: {
    position: 'relative',
    display: 'inline-flex',
    width: 120,
  },
  roleSelect: {
    width: '100%',
    appearance: 'none',
    WebkitAppearance: 'none',
    padding: '6px 28px 6px 10px',
    border: `1px solid ${colors.border}`,
    borderRadius: '6px',
    outline: 'none',
    background: '#FFFFFF',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '13px',
    color: colors.navy,
    '&:disabled': {
      cursor: 'default',
      opacity: 0.6,
    },
  },
  roleChevron: {
    position: 'absolute',
    top: '50%',
    right: 8,
    transform: 'translateY(-50%)',
    display: 'inline-flex',
    pointerEvents: 'none',
    color: colors.muted,
  },
  roleFixed: {
    paddingLeft: 2,
    fontSize: '13px',
    color: colors.muted,
  },
  joined: {
    fontSize: '13px',
    color: colors.muted,
  },
  more: {
    display: 'inline-flex',
    padding: 4,
    border: 0,
    borderRadius: '6px',
    background: 'transparent',
    cursor: 'pointer',
    color: colors.muted,
    '&:hover': {
      background: '#EEF1F5',
      color: colors.navy,
    },
  },
  menu: {
    minWidth: 180,
    padding: '6px',
    border: `1px solid ${colors.border}`,
    borderRadius: '12px',
    boxShadow:
      '0 20px 40px -4px rgba(145,158,171,.28), 0 0 2px rgba(145,158,171,.2)',
  },
  menuDanger: {
    width: '100%',
    height: 36,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '0 10px',
    border: 0,
    borderRadius: '8px',
    background: 'transparent',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    textAlign: 'left',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: 500,
    color: '#C9383B',
    '&:hover': {
      background: '#FDECEC',
    },
  },
  empty: {
    padding: '40px 20px',
    textAlign: 'center',
    fontSize: '14px',
    color: colors.muted,
  },
})

const initialsOf = text =>
  (text || '?')
    .split(/[\s@.]/)
    .filter(Boolean)
    .map(word => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

const formatDate = value =>
  value
    ? new Date(value).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '—'

/**
 * Workspace › Members: members with an inline role picker; pending
 * invitations are listed with a Pending badge.
 */
const MembersTable = ({
  members,
  roleOptions,
  getPermissionLabel,
  savingId,
  onRoleChange,
  onRemove,
}) => {
  const classes = useStyles()
  const [menu, setMenu] = useState(null) // { anchor, member }

  return (
    <Panel className={classes.root}>
      <div className={classes.title}>Members · {members.length}</div>
      <div className={classes.columns}>
        <span>Member</span>
        <span>Role</span>
        <span>Joined</span>
        <span />
      </div>
      {members.length === 0 && (
        <div className={classes.empty}>No members found in this workspace.</div>
      )}
      {members.map((member, index) => {
        const pending = member.status === 'Pending'
        const [background, color] = pending
          ? [colors.hover, colors.subtle]
          : AVATAR_COLORS[index % AVATAR_COLORS.length]
        const roleLabel = getPermissionLabel(member.resolvedPermissions)
        const preset = roleOptions.find(
          option => option.permissions === member.resolvedPermissions,
        )
        const editable = member.canManage && !pending && roleOptions.length > 0
        const key = member.userId || member.email

        return (
          <div key={key} className={classes.row}>
            <div className={classes.member}>
              <span className={classes.avatar} style={{ background, color }}>
                {initialsOf(
                  pending ? member.email : member.name || member.email,
                )}
              </span>
              {pending ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    minWidth: 0,
                  }}
                >
                  <span className={classes.pendingName}>{member.email}</span>
                  <span className={classes.pendingBadge}>Pending</span>
                </div>
              ) : (
                <div style={{ minWidth: 0 }}>
                  <div className={classes.name}>
                    {member.name || member.email}
                  </div>
                  {member.name && (
                    <div className={classes.email}>{member.email}</div>
                  )}
                </div>
              )}
            </div>

            <div>
              {editable ? (
                <span className={classes.roleWrap}>
                  <select
                    className={classes.roleSelect}
                    value={preset ? preset.permissions : 'custom'}
                    disabled={savingId === key}
                    onChange={event =>
                      onRoleChange(member, Number(event.target.value))
                    }
                  >
                    {!preset && (
                      <option value="custom" disabled>
                        {roleLabel}
                      </option>
                    )}
                    {roleOptions.map(option => (
                      <option key={option.name} value={option.permissions}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                  <span className={classes.roleChevron}>
                    <Icon icon="tabler:chevron-down" width={14} height={14} />
                  </span>
                </span>
              ) : (
                <span className={classes.roleFixed}>{roleLabel}</span>
              )}
            </div>

            <span className={classes.joined}>
              {pending ? 'Invited ' : ''}
              {formatDate(member.createdAt)}
            </span>

            <div>
              {member.canManage && (
                <button
                  type="button"
                  title="More actions"
                  className={classes.more}
                  onClick={event =>
                    setMenu({ anchor: event.currentTarget, member })
                  }
                >
                  <Icon icon="tabler:dots-vertical" width={16} height={16} />
                </button>
              )}
            </div>
          </div>
        )
      })}

      <Popover
        open={Boolean(menu)}
        anchorEl={menu?.anchor}
        onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ className: classes.menu, elevation: 0 }}
      >
        <button
          type="button"
          className={classes.menuDanger}
          onClick={() => {
            onRemove(menu.member)
            setMenu(null)
          }}
        >
          <Icon icon="tabler:trash" width={15} height={15} />
          {menu?.member?.status === 'Pending'
            ? 'Cancel invitation'
            : 'Remove from workspace'}
        </button>
      </Popover>
    </Panel>
  )
}

export default MembersTable
